import { Stack, Typography } from '@mui/material';
import {
  ISubject,
  IRecentRequests,
  ISubCategory,
  IDepartment,
} from 'API/UniversalRequest/universalRequest';
import { TAB_VALUE_TYPE } from '..';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { ParentCategoriesListItem } from './ParentCategoriesListItem';
import { useDictionary, useIsDesktop, useIsOtpLogin } from 'Hooks';
import {
  UniversalRequestSubject1,
  UniversalRequestSubject10,
  UniversalRequestSubject11,
  UniversalRequestSubject12,
  UniversalRequestSubject13,
  UniversalRequestSubject14,
  UniversalRequestSubject15,
  UniversalRequestSubject16,
  UniversalRequestSubject2,
  UniversalRequestSubject3,
  UniversalRequestSubject4,
  UniversalRequestSubject5,
  UniversalRequestSubject6,
  UniversalRequestSubject7,
  UniversalRequestSubject8,
  UniversalRequestSubject9,
} from 'Images';
import { useEffect, useState } from 'react';
import CustomNewAutoComplete from './CustomNewAutoComplete';

export const bySubjectIcons: { [key: number]: string } = {
  4: UniversalRequestSubject1,
  7: UniversalRequestSubject2,
  10: UniversalRequestSubject3,
  1: UniversalRequestSubject5,
  11: UniversalRequestSubject6,
  6: UniversalRequestSubject7,
  8: UniversalRequestSubject8,
  2: UniversalRequestSubject9,
  3: UniversalRequestSubject10,
  9: UniversalRequestSubject11,
  5: UniversalRequestSubject12,
  12: UniversalRequestSubject16,
  13: UniversalRequestSubject15,
};
export const byDepartmentIcons: { [key: number]: string } = {
  1: UniversalRequestSubject5,
  2: UniversalRequestSubject4,
  3: UniversalRequestSubject13,
  4: UniversalRequestSubject3,
  5: UniversalRequestSubject14,
  6: UniversalRequestSubject11,
  7: UniversalRequestSubject16,
};

interface ISubjectList {
  categoriesList: {
    subjects: ISubject[];
    departments: IDepartment[];
    subSubjects: ISubCategory[];
  };
  tabValue: TAB_VALUE_TYPE;
  specialCategoriesData: {
    data: IRecentRequests[];
    type: string;
  };
  handleCategoryClick: (categoryId: number | null) => void;
  handleSubCategoryClick: (subCategoryId: number | null) => void;
  handleTabChange: (_: any, newValue: TAB_VALUE_TYPE) => void;
}

export const ParentCategoriesList = ({
  categoriesList,
  tabValue,
  handleCategoryClick,
  handleSubCategoryClick,
  specialCategoriesData,
  handleTabChange,
}: ISubjectList) => {
  const { isOtp } = useIsOtpLogin();
  const dictionary = useDictionary(Dictionary, DICTIONARY.UniversalRequest);
  const generalDictionary = useDictionary(Dictionary, DICTIONARY.General);
  const isDesktop = useIsDesktop();
  const [showSuggested, setShowSuggested] = useState<boolean>(true);

  const [inputValue, setInputValue] = useState('');

  const getSubSubjectIdFromOptionValue = (option: string) =>
    categoriesList.subSubjects.find(
      (subSubject) =>
        `${subSubject.parentTitle} - ${subSubject.title}` === option
    )?.id || specialCategoriesData.data.find((cat) => cat.title === option)?.id;

  const getDepartmentIdFromOptionValue = (option: string) =>
    categoriesList.departments.find(
      (department) => `${department.title}` === option
    )?.id;

  const goToSelectedDepartment = (categoryId: number) => {
    handleTabChange(null, TAB_VALUE_TYPE.DEPARTMENT);
    handleCategoryClick(categoryId);
  };

  const onAutocompleteChange = (value: string | null) => {
    if (!value) return;

    const isSubSubject =
      value.includes('-') ||
      !!specialCategoriesData.data.find((cat) => cat.title === value);
    const selectedOptionId = isSubSubject
      ? getSubSubjectIdFromOptionValue(value)
      : getDepartmentIdFromOptionValue(value);
    if (!isOtp) {
      const selectedOption = isSubSubject
        ? categoriesList.subSubjects.find(
            (subSubject) => subSubject.id === selectedOptionId
          )
        : categoriesList.departments.find(
            (department) => department.id === selectedOptionId
          );
      if (isSubSubject && (selectedOption as ISubCategory)?.link)
        window.location.assign((selectedOption as ISubCategory)?.link || '');
      else if (selectedOption) goToSelectedDepartment(selectedOption.id);
    }
    if (!selectedOptionId) return;

    isSubSubject
      ? handleSubCategoryClick(selectedOptionId)
      : goToSelectedDepartment(selectedOptionId);
  };

  const getIconById = (id: number) =>
    (tabValue === TAB_VALUE_TYPE.SUBJECTS
      ? bySubjectIcons[id]
      : byDepartmentIcons[id]) || bySubjectIcons[1];

  useEffect(() => {
    if (inputValue) {
      setShowSuggested(false);
    } else {
      setShowSuggested(true);
    }
  }, [inputValue]);

  const getOptions = (): string[] => {
    const result = categoriesList.subSubjects.map((subSubject) => {
      return `${subSubject.parentTitle} - ${subSubject.title}`;
    });

    const departments = categoriesList.departments.map((department) => {
      return `${department.title}`;
    });

    return [...new Set(result), ...departments];
  };

  return (
    <Stack
      width={isDesktop ? '100%' : '91.875vw'}
      maxWidth={isDesktop ? '854px' : undefined}
      m='0 auto'
      pt='42px'
    >
      <CustomNewAutoComplete
        label={dictionary.documentSearch}
        options={getOptions().map((option) => ({
          label: option || '',
          value: option || '',
        }))}
        recentOptions={specialCategoriesData.data.map((category) => ({
          label: category.title || '',
          value: category.title || '',
        }))}
        setValue={onAutocompleteChange}
        groupName={
          specialCategoriesData.type === 'popular'
            ? dictionary.mayInterestYou
            : dictionary.recentRequests
        }
        getRequestId={(name: string) => {
          return specialCategoriesData.data.find(
            (category) => category.title === name
          )?.id;
        }}
      />
      <fieldset aria-describedby='select-parent-description'>
        <legend>
          {tabValue === TAB_VALUE_TYPE.SUBJECTS
            ? dictionary.whatsTheSubject
            : dictionary.whichDepartment}
        </legend>
        <span className='sr-only' id='select-parent-description'>
          {dictionary.selectParentDescription}
        </span>
        <Typography
          alignSelf='center'
          width={'100%'}
          variant={'body1'}
          m='42px 0 16px'
        >
          {tabValue === TAB_VALUE_TYPE.SUBJECTS
            ? dictionary.whatsTheSubject
            : dictionary.whichDepartment}
        </Typography>
        <Stack
          direction='row'
          width={'100%'}
          alignItems='center'
          justifyContent={isDesktop ? 'flex-start' : 'center'}
          flexWrap='wrap'
          m='0 auto'
          gap='16px'
          columnGap={isDesktop ? undefined : '16px'}
        >
          {categoriesList[
            tabValue === TAB_VALUE_TYPE.SUBJECTS ? 'subjects' : 'departments'
          ].map((category) => (
            <ParentCategoriesListItem
              key={category.id}
              category={category}
              onClick={() => handleCategoryClick(category.id)}
              svgIcon={getIconById(category.id)}
              tabValue={tabValue}
            />
          ))}
        </Stack>
      </fieldset>
    </Stack>
  );
};
